import config from '../config';
import { Milestone, Step, Action, FunctionDefinition, RenderableComponent } from './types';
import { stripClientIds } from './nba.utils';
import { localStorageKeys } from '../constants';

// --- Helper for making authenticated requests ---
const fetchWithAuth = async (url: string, options: RequestInit, ) => {
  const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
  if (!authToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: `Request failed with status: ${response.status}` }));
    throw new Error(errorData.message);
  }
  // For DELETE requests which have no content
  if (response.status === 204) {
    return;
  }
  return response.json();
};

// --- Milestone API Functions ---
export const createMilestone = async (milestoneData: Milestone) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name`;
  const body = stripClientIds(milestoneData);
  return fetchWithAuth(API_URL, { method: 'POST', body: JSON.stringify(body) });
};

export const updateMilestone = async (originalName: string, milestoneData: Milestone) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(originalName)}`;
  const body = stripClientIds(milestoneData);
  return fetchWithAuth(API_URL, { method: 'PUT', body: JSON.stringify(body) });
};

export const deleteMilestoneByName = async (milestoneName: string) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}`;
  return fetchWithAuth(API_URL, { method: 'DELETE' });
};

// --- Step API Functions ---
export const createStep = async (milestoneName: string, stepData: Step) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}/steps`;
  const body = stripClientIds(stepData);
  return fetchWithAuth(API_URL, { method: 'POST', body: JSON.stringify(body) });
};

export const updateStep = async (milestoneName: string, originalStepName: string, stepData: Step) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}/steps/${encodeURIComponent(originalStepName)}`;
  const body = stripClientIds(stepData);
  return fetchWithAuth(API_URL, { method: 'PUT', body: JSON.stringify(body) });
};

export const deleteStepByName = async (milestoneName: string, stepName: string) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}/steps/${encodeURIComponent(stepName)}`;
  return fetchWithAuth(API_URL, { method: 'DELETE' });
};

// --- Action API Functions ---
export const createAction = async (milestoneName: string, stepName: string, actionData: Action) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}/steps/${encodeURIComponent(stepName)}/actions`;
  const body = stripClientIds(actionData);
  return fetchWithAuth(API_URL, { method: 'POST', body: JSON.stringify(body) });
};

export const updateAction = async (
  milestoneName: string, stepName: string, originalActionName: string, actionData: Action
) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}/steps/${encodeURIComponent(stepName)}/actions/${encodeURIComponent(originalActionName)}`;
  const body = stripClientIds(actionData);
  return fetchWithAuth(API_URL, { method: 'PUT', body: JSON.stringify(body) });
};

export const deleteActionByName = async (milestoneName: string, stepName: string, actionName: string) => {
  const API_URL = `${config.gptServiceUrl}/configurations/by-name/${encodeURIComponent(milestoneName)}/steps/${encodeURIComponent(stepName)}/actions/${encodeURIComponent(actionName)}`;
  return fetchWithAuth(API_URL, { method: 'DELETE' });
};

// --- Guide Fetch (Remains the same) ---
export const fetchNbaGuide = async (authToken: string): Promise<any[]> => {
  const API_URL = `${config.gptServiceUrl}/configurations/guide`;
  const data = await fetchWithAuth(API_URL, { method: 'GET' });
  return data.milestones || [];
};

export const fetchFunctions = async (authToken: string): Promise<FunctionDefinition[]> => {
  const API_URL = `${config.gptServiceUrl}/functions`;
  try {
    const functions = await fetchWithAuth(API_URL, { method: 'GET' });
    return functions || [];
  } catch (error) {
    console.error('An API or network error occurred while fetching functions:', error);
    throw error;
  }
};

export const fetchComponents = async (authToken: string): Promise<RenderableComponent[]> => {
  const API_URL = `${config.gptServiceUrl}/components`;
  try {
    const components = await fetchWithAuth(API_URL, { method: 'GET' });
    return components || [];
  } catch (error) {
    console.error('An API or network error occurred while fetching components:', error);
    throw error;
  }
};